import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    LayoutDashboard,
    Boxes,
    Utensils,
    BarChart3,
    LogOut,
    Monitor,
    Users,
    Cpu,
    Settings,
    ScrollText,
    Menu as MenuIcon,
    X,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useAuth } from "@/context/AuthContext";
import DashboardView from "@/pages/admin/DashboardView";
import InventoryView from "@/pages/admin/InventoryView";
import MenusView from "@/pages/admin/MenusView";
import ReportsView from "@/pages/admin/ReportsView";
import StudentsView from "@/pages/admin/StudentsView";
import DevicesView from "@/pages/admin/DevicesView";
import SettingsView from "@/pages/admin/SettingsView";
import AuditView from "@/pages/admin/AuditView";

const BIOFOOD_LOGO = "/biofood-blue.png";

const NAV = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "students", label: "Estudiantes", icon: Users },
    { id: "inventory", label: "Inventario", icon: Boxes },
    { id: "menus", label: "Menús", icon: Utensils },
    { id: "devices", label: "Dispositivos", icon: Cpu },
    { id: "reports", label: "Reportes", icon: BarChart3 },
    { id: "audit", label: "Auditoría", icon: ScrollText },
    { id: "settings", label: "Configuración", icon: Settings },
];

export default function AdminApp() {
    const { user, logout } = useAuth();
    const [section, setSection] = useState("dashboard");
    const [open, setOpen] = useState(false);
    const canPos = user?.role === "cafeteria_operator" || user?.role === "super_admin";

    const SidebarItems = () => (
        <>
            {NAV.map((n) => (
                <button
                    key={n.id}
                    data-testid={`admin-nav-${n.id}`}
                    onClick={() => { setSection(n.id); setOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-bio text-sm font-semibold transition-colors ${
                        section === n.id
                            ? "bg-[#EEF7FC] text-bio-primary border-l-4 border-bio-primary"
                            : "text-bio-text-secondary hover:bg-bio-bg-alt hover:text-bio-primary"
                    }`}
                >
                    <n.icon className="h-4 w-4 shrink-0" /> <span className="truncate">{n.label}</span>
                </button>
            ))}
            {canPos && (
                <Link to="/pos" className="w-full flex items-center gap-3 px-4 py-3 rounded-bio text-sm font-semibold text-bio-text-secondary hover:bg-bio-bg-alt hover:text-bio-primary">
                    <Monitor className="h-4 w-4" /> Abrir POS
                </Link>
            )}
        </>
    );

    return (
        <div className="min-h-screen bg-bio-bg font-poppins text-bio-text overflow-x-hidden">
            {/* Topbar */}
            <header className="sticky top-0 z-30 h-16 bg-white/95 backdrop-blur-md border-b border-bio-border-subtle">
                <div className="h-full px-4 md:px-6 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <button className="lg:hidden p-1.5 -ml-1 text-bio-text" onClick={() => setOpen(!open)} data-testid="admin-menu-toggle">
                            {open ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
                        </button>
                        <img src={BIOFOOD_LOGO} alt="BIOFOOD" className="h-8 w-auto" />
                        <span className="hidden sm:block h-5 w-px bg-bio-border-subtle" />
                        <p className="hidden sm:block text-[11px] text-bio-text-secondary font-medium">{user?.role_label}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <LanguageToggle />
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button data-testid="user-menu" className="h-9 w-9 rounded-full bg-bio-gradient text-white font-bold text-sm flex items-center justify-center">{(user?.name || "U").charAt(0)}</button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-bio-card">
                                <div className="px-3 py-2"><p className="text-sm font-semibold">{user?.name}</p><p className="text-xs text-bio-text-muted">{user?.email}</p></div>
                                <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-bio-error cursor-pointer"><LogOut className="h-4 w-4 mr-2" /> Cerrar sesión</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            <div className="flex">
                {open && <div className="lg:hidden fixed inset-0 top-16 z-10 bg-black/30" onClick={() => setOpen(false)} />}
                <aside className={`${open ? "block" : "hidden"} lg:block w-[240px] shrink-0 bg-white p-3 space-y-1 border-r border-bio-border-subtle overflow-y-auto fixed lg:sticky top-16 bottom-0 lg:bottom-auto left-0 z-20 lg:z-auto lg:h-[calc(100vh-64px)] shadow-bio-modal lg:shadow-none`}>
                    <SidebarItems />
                </aside>

                <main className="flex-1 min-w-0 px-4 md:px-8 py-6 overflow-x-hidden">
                    {section === "dashboard" && <DashboardView />}
                    {section === "students" && <StudentsView />}
                    {section === "inventory" && <InventoryView />}
                    {section === "menus" && <MenusView />}
                    {section === "devices" && <DevicesView />}
                    {section === "reports" && <ReportsView />}
                    {section === "audit" && <AuditView />}
                    {section === "settings" && <SettingsView />}
                </main>
            </div>
        </div>
    );
}
